import { DOCS } from './docs'
import { getNodeCatalog } from './node-catalog-prompt'
import { getRouteCatalog } from './route-catalog-prompt'

/**
 * Appends tool-specific context to a system prompt based on which tools are present.
 * The companion's prompt builder calls it with the turn's loaded and indexed tool names.
 *
 * Note: column catalogs are NOT injected here. They're delivered on demand via
 * tool results (add_panel returns the new panel's catalog; set_filters returns
 * the relevant catalog inline when a column key is invalid). That keeps the
 * prompt lean and ensures the catalog matches the panel actually being modified.
 */
export function appendToolContext(prompt: string, toolNames: string[]): string {
  const extras: string[] = []
  const toolSet = new Set(toolNames)

  // Include node catalog when backtest tools are active
  if (
    toolSet.has('add_node') ||
    toolSet.has('connect_nodes') ||
    toolSet.has('create_strategy_template') ||
    toolSet.has('create_strategy_from_recipe') ||
    toolSet.has('create_saved_strategy') ||
    toolSet.has('edit_saved_strategy')
  ) {
    extras.push(getNodeCatalog())
  }

  if (toolSet.has('create_saved_strategy')) {
    extras.push(
      '## saved backtests\nOn any page, use create_saved_strategy, read_saved_strategy, edit_saved_strategy, and run_saved_strategy for an owned saved graph. Read its graphRevision before edits or runs. These tools do not require navigation or a mounted editor. Editor graph tools affect only the visible editor graph. After creating a strategy, return its Build link.',
    )
  }

  // Include route catalog when navigate_to is active and a catalog has been injected
  if (toolSet.has('navigate_to')) {
    const routeCatalog = getRouteCatalog()
    if (routeCatalog) {
      extras.push(routeCatalog)
    }
  }

  if (extras.length === 0) return prompt
  return prompt + '\n\n' + extras.join('\n\n')
}

/** Context handed to the grounding provider to decide whether/what to inject. */
export interface GroundingProviderContext {
  /** Active + indexed tool names for the turn. */
  toolNames: string[]
  /** Classify intent: "create" | "modify" | "read" | null. */
  intent?: string | null
  /** The user's raw request text. */
  intentText: string
}

/**
 * Grounding provider — supplied by the host app (frontend cookbook module) so
 * edge-ai never imports app code. Returns the exemplar block to inject, or ''
 * when the turn isn't a strategy-generation request. Mirrors the setNodeCatalog
 * injection pattern.
 */
let groundingProvider: ((ctx: GroundingProviderContext) => string) | null = null

/** Register the cookbook few-shot grounding provider (called once by the host app). */
export function setGroundingProvider(fn: (ctx: GroundingProviderContext) => string): void {
  groundingProvider = fn
}

/**
 * Appends cookbook few-shot exemplars to the prompt when the registered
 * grounding provider decides the turn warrants them (strategy-generation
 * intent). Zero cost when no provider is registered or the provider returns ''.
 */
export function appendGroundingContext(prompt: string, ctx: GroundingProviderContext): string {
  if (!groundingProvider) return prompt
  const block = groundingProvider(ctx)
  if (!block) return prompt
  return prompt + '\n\n' + block
}

/** Returns the list of all available doc keys (for classify stage) */
export function getAvailableDocKeys(): string[] {
  return Object.keys(DOCS)
}
